import * as React from 'react';
import { connect } from 'react-redux';
import type { RootState, Dispatch } from '../../../app/store';
import fetchFrom from '../../../utils/fetchData';
import { getFormattedPrice } from '../../../utils/formattedPrice';
import Header from '../../layout/Header';
import styles from './Confirmation.module.css';
import { useParams } from 'react-router-dom';
import loader from '../../../images/loading.gif';

interface ConfirmationT {
  getReceipt: (storeId: string, orderId: string) => void;
  receipt: any;
}

/**
 * The following feature has been removed due to lack of implementation:
 * - `<OrderStatus status={orderStatus} />`: This would display the current status of the order.
 * - Fetching order status from the endpoint `/order/${orderId}/${storeId}/preparingStatus`:
 *   This endpoint can be used to poll and update the order status in real-time.
 *   Unknown = 0,
 *   Initialized = 100,
 *   Preparing = 200,
 *   ReadyForPickUp = 300,
 *   Completed = 400,
 *   Cancelled = 500
 */

function Confirmation ({
  receipt,
  getReceipt,
}: ConfirmationT) {
  const { orderId, storeId } = useParams()
  const { loopNumber, orderItems, totalPrice, paymentInformation, totalDiscount
  } = receipt?.order || {};
  const [email, setEmail] = React.useState('')
  const [isValidEmail, setIsValidEmail] = React.useState<boolean | null>(null)
  const [emailSent, setEmailSent] = React.useState(false)
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  React.useEffect(() => {
    if (paymentInformation) {
      paymentInformation.paymentStatus === 200 && localStorage.removeItem('cartId')
    }
  }, [paymentInformation])

React.useEffect(() => {
  if (!storeId || !orderId || intervalRef.current) return

  intervalRef.current = setInterval(() => {
      getReceipt(storeId, orderId)
  }, 3000)

  return () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };
}, [storeId, orderId, getReceipt])

React.useEffect(() => {
  if (loopNumber && loopNumber > 0 && intervalRef.current) {
  clearInterval(intervalRef.current)
  intervalRef.current = null
}
}, [loopNumber])


  function validate () {
    return /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\d-]+\.)+[a-zA-Z]{2,}))$/.test(email)
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (validate()) {
      setIsValidEmail(true)
      await fetchFrom(`/order/${orderId}/${storeId}/email`, {
        method: 'PUT',
        body: { email }
      })
        setEmailSent(true)
    } else {
      setIsValidEmail(false)
    }
  }

  return (
    <>
      <div className={styles.page}>
        <Header withCancelButton={false} />
        <div className={styles.pageContent}>
          <section className={styles.checkout}>
            <div className={styles.content}>
              <h1 className={styles.heading}>Tack för ditt köp!</h1>
              <p className={styles.preamble}>
                Ditt ordernummer
              </p>
              <div className={styles.orderId}>
                {loopNumber ? loopNumber : 
                <div className={styles.loader}><img src={loader} alt="laddningsikon" /></div>}
              </div>
            </div>
          </section>
          <p className={styles.sectionHeading}>Orderdetaljer</p>
          <section className={styles.checkout}>
            {orderItems ?
              <>
                <div className={styles.details}>
                  <ul className={styles.detailsList}>
                    {orderItems.map(item => {
                      return item.itemId &&
                        <li className={styles.cartItem} key={item.id}>
                          <div className={styles.itemRow}>
                            <div className={styles.cartItemInfo}>
                              <span className={styles.productName}>{item.displayName}</span>
                              <span>{getFormattedPrice(item.unitPrice / 100)}</span>
                            </div>
                            {item.customizationMenuItems && item.customizationMenuItems.length > 0 && item.customizationMenuItems.map((ingredient) => {
                              if (!ingredient.preSelected || ingredient.price !== 0) {
                                return (
                                  <div key={ingredient.id} className={styles.extraIngredientRow}>
                                    <span className={styles.extraIngredient}>{ingredient.customizationType === 1 ? '+' : ingredient.customizationType === 2 ? '-' : ''} {ingredient.name}</span>
                                    <span className={styles.extraIngredientPrice}>{getFormattedPrice(ingredient.price / 100)}</span>
                                  </div>
                                )
                              }
                            }
                            )}
                          </div>
                        </li>
                    }
                    )}
                    {totalDiscount > 0 && (
                      <div className={styles.discountTotal}>
                        <span className={styles.discount}>Rabatt</span>
                        <span className={styles.discount}> -{getFormattedPrice(totalDiscount / 100)}</span>
                      </div>
                    )}
                  </ul>
                  <div className={styles.cartTotal}>
                    <p className={styles.cartInfo}>Totalt</p>
                    <p className={styles.cartItemInfo}>{getFormattedPrice(totalPrice / 100)}</p>
                  </div>
                </div>
              </> :
              <div className={styles.loader}><img src={loader} alt="laddningsikon" /></div>
            }

          </section>
          <p className={styles.sectionHeading}>Kvitto</p>
          <section className={styles.checkout}>
            <div className={styles.details}>
              <p className={styles.preamble}>
                Skicka kvitto till min mejladress
              </p>
              <form onSubmit={(e) => handleSubmit(e)} className={styles.emailInput}>
                <input
                  type='email'
                  className={isValidEmail === false ? styles.errorValueBox
                    : isValidEmail === true ?
                      styles.validValueBox
                      : styles.valueBox}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailSent(false); setIsValidEmail(true)}} 
                  />
                  {!isValidEmail && email.length > 0 && <p className={styles.errorMessage}>Ange en giltig mejladress</p>}
                  {emailSent && <p className={styles.emailSent}>Kvitto skickat till {email}</p>}
                <button className={styles.button} type="submit" onClick={handleSubmit}>Skicka kvitto</button>
              </form>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function mapState (state: RootState) {
  return {
    receipt: state.order.receipt,
  };
}
function mapDispatch (dispatch: Dispatch) {
  return {
    getReceipt: dispatch.order.getReceipt,
  };
}

export default connect(mapState, mapDispatch)(Confirmation);
